import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import debounce from 'lodash/debounce';

import { Button, FormGroup, FormControl, InputGroup } from 'react-bootstrap';

import { intlModule, pimModule, getLogger } from '@chedri/base';

import Icon from 'components/Icon';
import IconLink from 'components/IconLink';
import IconButton from 'components/IconButton';
import ContactModal from 'components/modals/ContactModal';
import SimplePrompt from 'components/layout/SimplePrompt';
import RichEditorContent from 'components/RichEditorContent';
import Pagination from 'components/layout/Pagination';
import { connect } from 'react-redux';
import { getContactList } from '../../../actions/ContactListActions';
import Loader from './../Loader';
import {changeActivePage} from "actions/ListActions";

const logger = getLogger('pim:contacts');

ContactList.propTypes = {
  showAddContact: PropTypes.bool,
};

ContactList.defaultProps = {
  showAddContact: true,
};

function markupMatch(match, offset, origin) {
  if (match) {
    return `<span class="highlight">${match}</span>`;
  }
  return '';
}

function highlight(entries, search) {
  return entries.map(entry => {
    const result = { __origin: entry };
    Object.keys(entry).forEach(key => {
      if (typeof entry[key] === 'string') {
        result[key] = entry[key].replace(new RegExp('^' + search, 'i'), markupMatch);
      } else {
        result[key] = entry[key];
      }
    });
    return result;
  });
}

function filterNumbersWithFallback(numbers, types) {
  if (Array.isArray(numbers) && Array.isArray(types)) {
    for (let i = 0, n = types.length; i < n; i++) {
      const found = numbers.find(entry => entry.type === types[i]);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

const phoneTypesFallback = ['mobile', 'headquarter', 'phone', 'extension-number'];

function ContactList({ showAddContact, onSelected, getContactList, pending, contacts, limit, count, page }) {

  const dispatch = useDispatch();

  const messages = useSelector(intlModule.selectors.getMessages);

  const canFetchNextChunk = useSelector(pimModule.contacts.selectors.canFetchNextChunk);

  const [searchText, setSearchText] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [selectedContact, setSelectedContact] = useState(undefined);

  const [showContactModal, setShowContactModal] = useState(false);

  const searchInputChange = useCallback(
    ({ target }) => {
      if (target && target.value != null) {
        setSearchInput(target.value);
        search(target.value);
      }
    },
    [search, setSearchInput]
  );

  const handlePagination = nextPage => {
    getContactList(limit, 'relevance', 'desc', nextPage, limit * (nextPage - 1));
  };

  const searchInputReset = useCallback(() => {
    setSearchInput('');
    search('');
  }, [search, setSearchInput]);

  const addContact = useCallback(() => {
    setShowContactModal(true);
  }, [setShowContactModal]);

  const deleteContact = useCallback(contact => {
    dispatch(pimModule.contacts.delete(contact));
  }, []);

  const closeContactModal = useCallback(
    contact => {
      setShowContactModal(false);
      setSelectedContact(undefined);
    },
    [setShowContactModal]
  );

  const search = useCallback(
    debounce(async search => {
      if (search != null && search.length > 0) {
        dispatch(pimModule.contacts.fetchList({ filters: { search } }));
        setSearchText(search);
      } else {
        dispatch(pimModule.contacts.fetchList());
        setSearchText('');
      }
    }, 400),
    [contacts, setSearchText]
  );

  const selectContact = useCallback(contact => {
    return () => {
      return typeof onSelected === 'function' ? onSelected(contact.__origin || contact) : null;
    };
  }, []);

  useEffect(() => {
    getContactList(20, 'relevance', 'desc', 1, 0);
  }, []);
  
  return (
    <div fluid className="contacts">
      <div className="contacts-header">
        <div className="search-label">
          <h4>
            <Icon name="fa-address-book" label={messages.contacts} />
          </h4>
        </div>
        <div className="search">
          <FormGroup controlId="searchInputId">
            <InputGroup>
              <InputGroup.Addon
                style={{ background: 'white', borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' }}
              >
                <Icon name={pending ? 'fa-spinner-third fa-spin' : 'fa-search'} />
              </InputGroup.Addon>
              <FormControl type="text" value={searchInput} onChange={searchInputChange} autocomplete="off" />
              <InputGroup.Addon
                style={{ background: 'white', borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }}
              >
                <IconLink name="fa-times" onClick={searchInputReset} />
              </InputGroup.Addon>
            </InputGroup>
          </FormGroup>
        </div>
        <div className="actions">
          <div className="inline m-l-10">
            <Pagination count={count} resPerPage={20} activePage={page} handleSelect={handlePagination} />
          </div>
          {showAddContact && (
            <IconButton
              name="fa-plus"
              iconClassName="m-r-10 sm-m-r-0"
              buttonClassName="btn btn-primary btn-rounded"
              label={messages.add}
              onClick={addContact}
            />
          )}
        </div>
      </div>
      {Array.isArray(contacts) &&
        (pending ? (
          <Loader />
        ) : (
          contacts.map(contact => {
            const displayNumber = filterNumbersWithFallback(contact.numbers, phoneTypesFallback);
            const { first_name: firstName, last_name: lastName } = contact;
            const fullName = [firstName, lastName].filter(name => !!name).join(' ');
            return (
              <div key={contact.id} className="list-entry" onClick={selectContact(contact)}>
                <div className="list-entry-item">
                  <RichEditorContent content={fullName} />
                </div>
                <div className="list-entry-item">
                  <RichEditorContent content={contact.company || ''} />
                </div>
                <div className="list-entry-item">
                  <p>
                    <span>
                      {contact?.addresses?.[0]?.street ? contact.addresses[0].street : ''}{' '}
                      {contact?.addresses?.[0]?.house_number ? contact.addresses[0].house_number : ''}{' '}
                      {contact?.addresses?.[0]?.apartment_number ? `- ${contact.addresses[0].apartment_number}` : ''}
                    </span>
                  </p>
                  <p>
                    <span>
                      {contact?.addresses?.[0]?.address_line2 ? contact.addresses[0].address_line2 : ''}{' '}
                    </span>
                  </p>
                  <p>
                    <span>
                      {contact?.addresses?.[0]?.zipcode ? contact.addresses[0].zipcode : ''}{' '}
                      {contact?.addresses?.[0]?.city ? contact.addresses[0].city : ''}
                    </span>
                  </p>
                  <p>
                    <span>
                      {contact?.addresses?.[0]?.country ? contact.addresses[0].country : ''}
                    </span>
                  </p>
                </div>
                <div className="list-entry-item">
                  {displayNumber && (
                    <div className="number">
                      <div className="number-type">
                        <div>{`${messages[displayNumber.type]}`}</div>
                      </div>
                      <div className="number-content">
                        <a href={`tel:${displayNumber.number}`}>
                          <RichEditorContent content={displayNumber.number || ''} />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                <div className="list-entry-item">
                  <a href={`mailto:${contact.email}`}>
                    <RichEditorContent content={contact.email || ''} />
                  </a>
                </div>
                <div className="list-entry-item actions">
                  <Button
                    className="btn-rounded m-r-10"
                    onClick={() => {
                      setSelectedContact(contact);
                      setShowContactModal(true);
                    }}
                  >
                    {messages.edit}
                  </Button>
                  <SimplePrompt onSuccess={() => deleteContact(contact)} />
                </div>
              </div>
            );
          })
        ))}
      <ContactModal
        show={showContactModal}
        contact={selectedContact ? selectedContact.__origin || selectedContact : undefined}
        onClose={closeContactModal}
      />
    </div>
  );
}

const mapStateToProps = state => ({
  pending: state.contacts.pending,
  contacts: state.contacts.contacts,
  count: state.contacts.count,
  limit: state.contacts.limit,
  offset: state.contacts.offset,
  page: state.contacts.page,
});

const mapDispatchToProps = dispatch => {
  return {
    changeActivePage: (listName, activePage, resPerPage) => {
      dispatch(changeActivePage(listName, activePage, resPerPage));
    },
    getContactList: (limit, sort, dir, page, offset) => {
      dispatch(getContactList(limit, sort, dir, page, offset));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ContactList);
